import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { t } from '../i18n';
import { formatDate } from '@werkdelta/shared';
import { Feld, Fehler } from '../components/ui';
import type { Me } from '../App';

export function CasesPage({ me }: { me: Me }) {
  const [cases, setCases] = useState<any[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [caseNumber, setCaseNumber] = useState('');
  const [title, setTitle] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get<any[]>(`/api/v1/cases${statusFilter ? `?status=${statusFilter}` : ''}`)
      .then(setCases)
      .catch(() => setCases([]));
  }, [statusFilter]);

  async function createCase(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const created = await api.post('/api/v1/cases', { caseNumber, title });
      navigate(`/faelle/${created.id}/schritt/1`);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    }
  }

  const canWrite = me.role !== 'LESER' && !me.organization.licenseExpired;

  return (
    <>
      <h1>{t('cases.title')}</h1>
      <div className="aktionen" style={{ marginBottom: '1rem' }}>
        {canWrite && (
          <button type="button" className="knopf-primaer" onClick={() => setShowForm((s) => !s)}>
            {t('cases.new')}
          </button>
        )}
        <label style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          {t('cases.filterStatus')}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ font: 'inherit', minHeight: '44px', padding: '0.4rem 0.6rem' }}
          >
            <option value="">{t('cases.filterAll')}</option>
            {['OFFEN', 'IN_BEARBEITUNG', 'ABGESCHLOSSEN', 'ARCHIVIERT'].map((s) => (
              <option key={s} value={s}>
                {t(`cases.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {showForm && (
        <form className="karte" onSubmit={createCase}>
          <h2>{t('cases.new')}</h2>
          <div className="feld-zeile">
            <Feld label={t('cases.caseNumber')} required hint={t('cases.caseNumberHint')}>
              <input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} required />
            </Feld>
            <Feld label={t('cases.caseTitle')} required>
              <input value={title} onChange={(e) => setTitle(e.target.value)} required />
            </Feld>
          </div>
          <Fehler code={error} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">
              {t('cases.createAndStart')}
            </button>
            <button type="button" className="knopf-sekundaer" onClick={() => setShowForm(false)}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}
      {cases === null ? (
        <p>{t('app.loading')}</p>
      ) : cases.length === 0 ? (
        <div className="leer-hinweis">{t('cases.empty')}</div>
      ) : (
        <div className="tabelle-scroll">
        <table className="daten">
          <thead>
            <tr>
              <th>{t('cases.caseNumber')}</th>
              <th>{t('cases.caseTitle')}</th>
              <th>{t('cases.client')}</th>
              <th>{t('common.status')}</th>
              <th>{t('cases.updated')}</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/faelle/${c.id}/schritt/1`}>{c.caseNumber}</Link>
                  {c.isDemo && (
                    <span className="badge warn" style={{ marginLeft: '0.5rem' }}>
                      DEMO
                    </span>
                  )}
                </td>
                <td>{c.title}</td>
                <td>{c.clientName ?? '—'}</td>
                <td>
                  <span className="badge">{t(`cases.status.${c.status}`)}</span>
                </td>
                <td>{formatDate(c.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </>
  );
}
